import differenceInMonths from 'date-fns/differenceInMonths';
import differenceInYears from 'date-fns/differenceInYears';
import { translate as t } from '@fiverr-private/i18n-react';
import { getContext } from '@fiverr-private/fiverr_context';
import { EmploymentType, WorkExperience } from '../../../gql-operations/gql-generated';

export const T_KEY = 'seller_pages_toolbox.work_experience';
export const T_FORM_KEY = `${T_KEY}.form`;

interface DateRangeOptions {
    onlyDuration?: boolean;
}

export const formatDateRange = (
    startDate?: number | null,
    endDate?: number,
    { onlyDuration }: DateRangeOptions = {}
) => {
    if (!startDate) {
        return '';
    }

    const { locale } = getContext();
    const T_KEY_WORK_TIME = `${T_KEY}.work_time`;
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const formatter = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        timeZone: 'UTC',
    });

    const startFormatted = formatter.format(start);
    const endFormatted = formatter.format(end);

    const months = (differenceInMonths(end, start) || 1) % 12;
    const years = differenceInYears(end, start);

    let durationText = '';
    if (years > 0) {
        durationText = `${years} ${
            years > 1 ? t(`${T_KEY_WORK_TIME}.years_abbreviation`) : t(`${T_KEY_WORK_TIME}.year_abbreviation`)
        }`;
        if (months > 0) {
            durationText += ` ${months} ${t(`${T_KEY_WORK_TIME}.${months === 1 ? 'month' : 'months'}_abbreviation`)}`;
        }
    } else if (months > 0) {
        durationText = `${months} ${t(`${T_KEY_WORK_TIME}.${months === 1 ? 'month' : 'months'}_abbreviation`)}`;
    }

    if (onlyDuration) {
        return durationText;
    }

    const presentText = endDate ? '' : t(`${T_KEY_WORK_TIME}.present`);

    return `${startFormatted} - ${endDate ? endFormatted : presentText} · ${durationText}`;
};

export const employmentTypeEnumToTranslationKey = {
    [EmploymentType.FullTime]: 'full_time',
    [EmploymentType.PartTime]: 'part_time',
    [EmploymentType.Freelance]: 'freelance',
    [EmploymentType.Unspecified]: 'unspecified',
    [EmploymentType.SelfEmployed]: 'self_employed',
};

export const shouldDisplayEmploymentType = (experience: WorkExperience): boolean =>
    experience.employmentType !== undefined && experience.employmentType !== EmploymentType.Unspecified;

export const EXPERIENCES_TO_SHOW = 3;

export const getWorkExperienceAmount = (workExperiences?: WorkExperience[] | null) =>
    workExperiences?.length && workExperiences?.length > EXPERIENCES_TO_SHOW
        ? EXPERIENCES_TO_SHOW
        : workExperiences?.length || 0;

export const buildDate = (year: string, month: string) => new Date(Date.UTC(Number(year), Number(month), 1));

export const IMPORT_TIMEOUT = 60000;

export enum LinkedInImportStatusEvent {
    PROFILE_FETCH_SUCCESS = 'PROFILE_FETCH_SUCCESS',
    PROFILE_FETCH_FAILED = 'PROFILE_FETCH_FAILED',
    PROFILE_NOT_FOUND = 'PROFILE_NOT_FOUND',
}

export const workExperienceChronologicalComparator = (
    a: Pick<WorkExperience, 'startDate' | 'endDate'>,
    b: Pick<WorkExperience, 'startDate' | 'endDate'>
): number => {
    if (!a.endDate && b.endDate) {
        return -1;
    }

    if (a.endDate && !b.endDate) {
        return 1;
    }

    const aEndDate = a.endDate ?? 0;
    const bEndDate = b.endDate ?? 0;

    if (aEndDate === bEndDate) {
        return (b.startDate ?? 0) - (a.startDate ?? 0);
    }

    return bEndDate - aEndDate;
};
